import { useState } from 'react';
import { outbox } from '../lib/outbox';
import { getPosition } from '../lib/native';
import type { Task } from '../lib/types';
import { PhotoPicker, type UploadedPhoto } from './PhotoPicker';
import { SwipeConfirm } from './SwipeConfirm';
import { RotateCw, Undo2, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/** 三次是 belowMaxAttempts guard 里的上限,和后端保持一致 */
const MAX_ATTEMPTS = 3;

/**
 * 投递失败后的出路。
 *
 * ★ 之前这里是个死胡同:按钮写着「再次投递」,点开却是送达/未送达的面板,
 *   选「已送达」会被后端拒(DELIVER 不接受 FAILED),提示「请刷新后重试」
 *   —— 而刷新永远也不会让它变成可送达。司机手里拿着货,界面没有任何出路。
 *
 * 现在给两条明确的路:
 *   · 再次投递 → RETRY(货在手上,再跑一趟)
 *   · 退回     → RETURN(终态,责任转移出司机)
 *
 * 达到失败上限后只留退回 —— 后端 guard 也会拒掉重投,
 * 界面上还留着按钮只会让司机白点一次。
 */
export function FailedActions({
  task,
  onDone,
}: {
  task: Task;
  onDone: () => Promise<void>;
}) {
  const [returning, setReturning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attemptsUsedUp = task.failedAttempts >= MAX_ATTEMPTS;

  async function retry() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await outbox.enqueue({
        orderIds: [task.id],
        action: 'RETRY',
        endpoint: `/orders/${task.id}/transitions`,
        body: { action: 'RETRY', idempotencyKey: '' },
      });
      await onDone();
    } catch (e) {
      setError((e as Error).message || '提交失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200
                      dark:border-red-900 px-3 py-2 text-sm text-red-800 dark:text-red-300">
        未送达 · 已尝试 {task.failedAttempts} / {MAX_ATTEMPTS} 次
        {attemptsUsedUp && ' · 已达上限,请退回'}
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!attemptsUsedUp && (
        <button
          onClick={() => void retry()}
          disabled={busy}
          className="w-full h-14 rounded-xl bg-brand-600 text-white text-lg font-medium
                     disabled:opacity-40 active:bg-brand-700 transition"
        >
          {busy ? '提交中…' : <><InlineIcon icon={RotateCw} /> 再次投递</>}
        </button>
      )}

      <button
        onClick={() => setReturning(true)}
        className="w-full h-14 rounded-xl border-2 border-red-300 dark:border-red-800
                   text-lg font-medium text-red-700 dark:text-red-400
                   active:bg-red-50 dark:active:bg-red-950/40 transition"
      >
        <InlineIcon icon={Undo2} /> 退回发货方
      </button>

      {returning && (
        <ReturnSheet task={task} onClose={() => setReturning(false)} onDone={onDone} />
      )}
    </div>
  );
}

/**
 * 退回确认。
 *
 * 退回是终态且货物责任要转移出司机,所以:
 *   · 原因必填(后端 requiresReason)
 *   · 用滑动确认防误触
 *   · 建议拍一张货物照片 —— 「退回时是什么状态」将来是纠纷焦点
 */
function ReturnSheet({
  task,
  onClose,
  onDone,
}: {
  task: Task;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const geo = await getPosition();
      await outbox.enqueue({
        orderIds: [task.id],
        action: 'RETURN',
        endpoint: `/orders/${task.id}/transitions`,
        body: {
          action: 'RETURN',
          idempotencyKey: '',
          reason: reason.trim(),
          ...(geo ? { geo } : {}),
        },
        photos: photos.map((p) => ({
          slot: 'delivery',
          purpose: 'FAIL_PHOTO',
          blob: p.blob,
        })),
      });
      await onDone();
      onClose();
    } catch (e) {
      setError((e as Error).message || '提交失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50" onClick={onClose}>
      <div
        className="w-full max-h-[90dvh] overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-800
                   p-4 pb-safe"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="退回发货方"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">退回发货方</h3>
          <button onClick={onClose} aria-label="关闭" className="text-2xl text-slate-400">
            <InlineIcon icon={X} />
          </button>
        </div>

        <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
          {task.receiverName} · 退回后这单结束,货物需交回发货方
        </p>

        {error && (
          <div className="mb-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <label className="block mb-3">
          <span className="block mb-1 text-sm font-medium text-slate-600 dark:text-slate-400">
            退回原因(必填)
          </span>
          <textarea
            aria-label="退回原因"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="例如:联系不上收件人,已尝试三次"
            className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-base"
          />
        </label>

        <div className="mb-4">
          <span className="block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
            货物照片(建议)
          </span>
          <PhotoPicker purpose="FAIL_PHOTO" max={3} photos={photos} onChange={setPhotos} />
          {/* 退回时货物是什么状态,将来出纠纷时是关键证据 */}
          <p className="mt-1.5 text-xs text-slate-400">
            拍一张能证明货物完好的照片,避免日后「退回时就破损了」的争议
          </p>
        </div>

        <SwipeConfirm
          label="滑动确认退回"
          disabled={busy || !reason.trim()}
          hint={!reason.trim() ? '请先填写退回原因' : undefined}
          onConfirm={() => void submit()}
        />
      </div>
    </div>
  );
}
